import { useState } from 'react'
import { useApplyDraft, useDraftPreview, useRejectDraft } from '../../domains/drafts/queries'
import { DRAFT_KIND_LABELS, type DraftChange } from '../../domains/drafts/types'
import { Banner, Button, Modal, SectionLabel } from '../../design/components'
import { describeApiFailure } from '../../lib/failure'
import styles from './drafts.module.css'

export interface DraftReviewModalProps {
  draftId: string
  onClose: () => void
  /** Called after a draft is applied, with the character it produced. */
  onApplied?: (characterId: number) => void
}

/**
 * The confirmation step, and the reason the assistant is allowed to write character sheets at all.
 *
 * The assistant cannot reach a character; it can only leave a draft here. What lands is what this
 * screen showed and somebody agreed to — so the screen has one job, which is to make the change
 * legible before it happens. Everything shown is server-computed: the diff, the labels, the
 * findings. The client renders and does not interpret.
 *
 * **Apply can still fail, and the failures are the interesting part.** A 409 means the character
 * moved on since the draft was prepared; a 410 means the draft is no longer on offer. Neither is
 * worth retrying, so both are shown as text and the dialog stays open on the stale content rather
 * than closing as if something happened.
 */
export function DraftReviewModal({ draftId, onClose, onApplied }: DraftReviewModalProps) {
  const preview = useDraftPreview(draftId)
  const apply = useApplyDraft()
  const reject = useRejectDraft()
  const [failure, setFailure] = useState<string | null>(null)

  const draft = preview.data?.draft
  const changes = preview.data?.changes ?? []
  const issues = preview.data?.issues ?? []
  const errors = issues.filter((issue) => issue.severity === 'Error')
  const warnings = issues.filter((issue) => issue.severity === 'Warning')
  const busy = apply.isPending || reject.isPending

  const title =
    draft?.kind === undefined
      ? 'Review change'
      : `Review ${DRAFT_KIND_LABELS[draft.kind].toLowerCase()}`

  function onApply() {
    setFailure(null)
    apply.mutate(draftId, {
      onSuccess: (envelope) => {
        const characterId = envelope.changed.core?.id
        if (characterId !== undefined) onApplied?.(characterId)
        onClose()
      },
      onError: (error) => setFailure(describeApiFailure(error, 'This is not your draft to apply.')),
    })
  }

  function onReject() {
    setFailure(null)
    reject.mutate(draftId, {
      onSuccess: onClose,
      onError: (error) =>
        setFailure(describeApiFailure(error, 'This is not your draft to turn down.')),
    })
  }

  return (
    <Modal title={title} onClose={onClose}>
      {preview.isPending && <p className={styles.muted}>Loading…</p>}

      {preview.isError && (
        <Banner tone="error">
          That draft is no longer there. It may have expired or been applied already.
        </Banner>
      )}

      {preview.isSuccess && draft !== undefined && (
        <div className={styles.review}>
          <p className={styles.summary}>{draft.summary}</p>

          {draft.characterName != null && (
            <p className={styles.muted}>
              {DRAFT_KIND_LABELS[draft.kind ?? 'CreateCharacter']} for {draft.characterName}
            </p>
          )}

          {/*
            Errors before the diff, not after it. A draft that cannot be applied should say so
            before somebody has read three screens of changes they are about to be refused.
          */}
          {errors.length > 0 && (
            <Banner tone="error">
              <strong>This cannot be applied.</strong>
              <ul className={styles.issues}>
                {errors.map((issue) => (
                  <li key={`${issue.code}-${issue.path ?? ''}`}>{issue.message}</li>
                ))}
              </ul>
            </Banner>
          )}

          {warnings.length > 0 && (
            <Banner tone="warn">
              <ul className={styles.issues}>
                {warnings.map((issue) => (
                  <li key={`${issue.code}-${issue.path ?? ''}`}>{issue.message}</li>
                ))}
              </ul>
            </Banner>
          )}

          <SectionLabel>What would change</SectionLabel>
          {changes.length === 0 ? (
            <p className={styles.muted}>Nothing to show — this draft is too old to read.</p>
          ) : (
            <ul className={styles.changes}>
              {changes.map((change) => (
                <ChangeRow key={change.path ?? change.label} change={change} />
              ))}
            </ul>
          )}

          {failure !== null && <Banner tone="error">{failure}</Banner>}

          {draft.status !== 'Pending' && (
            <Banner tone="info">
              This draft is {draft.status?.toLowerCase()} and can no longer be answered.
            </Banner>
          )}

          <div className={styles.actions}>
            <Button
              variant="outline"
              onClick={onReject}
              disabled={busy || draft.status !== 'Pending'}
            >
              Discard
            </Button>
            <Button onClick={onApply} disabled={busy || preview.data?.canApply !== true}>
              {apply.isPending ? 'Applying…' : 'Apply'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}

/**
 * One field. `current` is null for anything the character does not have yet — every row of a new
 * character, and most rows of the others — so the arrow is only drawn when there is something on
 * both sides of it to compare.
 */
function ChangeRow({ change }: { change: DraftChange }) {
  return (
    <li className={styles.change}>
      <span className={styles.changeLabel}>{change.label}</span>
      <span className={styles.changeValue}>
        {change.current != null && (
          <>
            <span className={styles.was}>{change.current}</span>
            <span aria-hidden="true"> → </span>
          </>
        )}
        <span>{change.proposed ?? '—'}</span>
      </span>
    </li>
  )
}
